import { Line2Struct, Segment2Struct, Vec2Struct } from './types';

/**
 * Returns the squared euclidean distance between two points. Prefer this when comparing distances in
 *   performance-critical paths, as it avoids a `Math.sqrt` call.
 */
export function vec2DistanceSquared(a: Vec2Struct, b: Vec2Struct): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return dx * dx + dy * dy;
}

/**
 * Returns the squared perpendicular distance from a point to an infinite line.
 *
 * Assumes `line.direction` is a unit vector.
 */
export function vec2DistanceToLineSquared(point: Vec2Struct, line: Line2Struct): number {
  const vx = point.x - line.basePoint.x;
  const vy = point.y - line.basePoint.y;
  const cross = vy * line.direction.x - vx * line.direction.y;
  return cross * cross;
}

/**
 * Returns the squared distance from a point to the closest point on a line segment.
 *
 * Assumes `segment` is a valid segment with a non-zero length.
 */
export function vec2DistanceToSegmentSquared(point: Vec2Struct, segment: Segment2Struct): number {
  const dx = segment.endPoint.x - segment.basePoint.x;
  const dy = segment.endPoint.y - segment.basePoint.y;
  const lengthSquared = dx * dx + dy * dy;

  // parameter t of the closest point on the infinite line, clamped to the segment
  const t = Math.max(
    0,
    Math.min(1, ((point.x - segment.basePoint.x) * dx + (point.y - segment.basePoint.y) * dy) / lengthSquared),
  );

  const closestX = segment.basePoint.x + t * dx;
  const closestY = segment.basePoint.y + t * dy;
  const ex = point.x - closestX;
  const ey = point.y - closestY;
  return ex * ex + ey * ey;
}
